import XlsxPopulate from 'xlsx-populate';
import Papa from 'papaparse';
import omitBy from 'lodash/omitBy';
import isEqual from 'lodash/isEqual';
import isString from 'lodash/isString';

import {
  CONDITIONAL_PLAN,
  CUSTOM_RATE_PLAN,
  FILE_SHEET_UPLOAD_ERROR_TYPES,
  FLAT_RATE_PLAN,
  FREE_PLAN,
  SHEET_MAX_NUM_OF_PRODUCTS
} from 'constants/products';
import { UPLOADED_FILE_TYPES } from 'constants/common';
import { COUNTRY_CURRENCY } from 'constants/helpers';

import { isDashboardArabic } from './intl-wrapper';
import { customizedFmt } from 'components/IntlWrapper/IntlWrapper';

const { XLSX, CSV } = UPLOADED_FILE_TYPES;

export const PRODUCT_OBJ_MAPPING = {
  0: 'name',
  1: 'nameAr',
  2: 'description',
  3: 'descriptionAr',
  4: 'refId',
  5: 'category',
  6: 'defaultPrice',
  7: 'originalPrice',
  8: 'availableQuantity',
  9: 'disabled',
  10: 'option1',
  11: 'option1Value',
  12: 'option2',
  13: 'option2Value',
  14: 'option3',
  15: 'option3Value',
  16: 'imageURL'
};

export const PRODUCT_HEADER_MAPPING = [
  'Name en',
  'اسم المنتج بالعربي',
  'Description',
  'وصف المنتج بالعربي',
  'ID',
  'Category',
  'Price',
  'Original price',
  'Quantity',
  'Active',
  'Option 1',
  'Option 1 value',
  'Option 2',
  'Option 2 value',
  'Option 3',
  'Option 3 value',
  'Image URL'
];

export const getSheetFileError = ({ header, rowsData }) => {
  const { INVALID_FILE, EMPTY_FILE, EXCEEDED_MAX_ROWS } =
    FILE_SHEET_UPLOAD_ERROR_TYPES;

  const fileHeaders = header
    .filter((colName) => colName != null)
    .map((colName) =>
      isString(colName) ? colName.split(/\n|\r/)[0] : colName
    );

  if (!isEqual(PRODUCT_HEADER_MAPPING, fileHeaders)) {
    return {
      errorType: INVALID_FILE
    };
  } else if (rowsData?.length < 1) {
    return {
      errorType: EMPTY_FILE
    };
  } else if (rowsData?.length > SHEET_MAX_NUM_OF_PRODUCTS) {
    return {
      errorType: EXCEEDED_MAX_ROWS,
      rowsData
    };
  }

  return null;
};

export const getCSVRowsData = (convertedCSV, mappingByIndex) => {
  const { data } = convertedCSV;
  const allRows = data.filter(
    (row) => Object.keys(omitBy(row, (item) => !item)).length
  );

  if (!mappingByIndex) {
    return allRows;
  } else {
    const rows = [];
    allRows.forEach((cells, index) => {
      if (index > 0) {
        const record = {};
        cells.forEach((cell, i) => {
          record[mappingByIndex[i]] = cell === '' ? undefined : cell;
        });
        rows.push(record);
      }
    });
    return rows;
  }
};

export const convertCSVToJSON = (
  file,
  config = {
    dynamicTyping: true
  }
) => {
  return new Promise((resolve, reject) => {
    Papa.parse(file, {
      config,
      complete: (result) => {
        const header = result?.data?.[0];
        const rowsData = getCSVRowsData(result, PRODUCT_OBJ_MAPPING);
        const formattedData = groupProductWithVariances(rowsData);
        const error = getSheetFileError({
          header,
          rowsData: formattedData
        });
        if (error) {
          reject(error);
        }
        return resolve(rowsData);
      }
    });
  });
};

export const getXlsxRowsData = (sheetRecords, mappingByIndex) => {
  const allRows = sheetRecords
    .slice(1)
    .filter((row) => Object.keys(omitBy(row, (item) => !item)).length)
    .map((row) => {
      const record = {};
      const r = row.map((cell, index) => {
        const c =
          cell instanceof XlsxPopulate.RichText
            ? cell.text().replace(/undefined/g, '')
            : cell;

        if (mappingByIndex) {
          record[mappingByIndex[index]] = c;
        }
        return c;
      });

      return mappingByIndex ? record : r;
    });

  return allRows;
};

const getOptionsOrder = (currentProduct, product) => {
  const updateOption = (optionName, optionValue, order) => {
    const formattedOptionName = toLowercaseAndCapitalizeFirstChar(optionName);
    return {
      [toLowercaseAndCapitalizeFirstChar(optionName)]: {
        order,
        values: [
          ...(currentProduct?.optionsOrder?.[formattedOptionName]?.values ||
            []),
          toLowercaseAndCapitalizeFirstChar(optionValue)
        ].filter(
          (value, index, self) =>
            value !== undefined &&
            self.findIndex(
              (v) => String(v).toLowerCase() === String(value).toLowerCase()
            ) === index
        )
      }
    };
  };

  const optionsOrder = {
    ...(!!currentProduct.option1 &&
      updateOption(currentProduct.option1, product.option1Value, 1)),
    ...(!!currentProduct.option2 &&
      updateOption(currentProduct.option2, product.option2Value, 2)),
    ...(!!currentProduct.option3 &&
      updateOption(currentProduct.option3, product.option3Value, 3))
  };
  return optionsOrder;
};

const getAllCombinations = (values) => {
  if (values.length === 0) return [];
  // Recursive function to get all possible combinations
  const combine = (existing, values) => {
    if (values.length === 0) return [existing.join(', ')];

    const result = [];
    const firstArray = values[0];
    const remainingArrays = values.slice(1);

    for (let value of firstArray) {
      result.push(...combine([...existing, value], remainingArrays));
    }
    return result;
  };
  return combine([], values);
};

const findVarianceOptions = (value, options) => {
  for (const key in options) {
    const lowerCaseValues = options[key].values.map((v) => v.toLowerCase());
    if (lowerCaseValues.includes(value.toLowerCase())) {
      return { optionName: key, optionValue: value };
    }
  }
  return null;
};

const getAllVariances = (options, currentVariances) => {
  if (!options) {
    return currentVariances;
  }

  const keys = Object.keys(options);
  const uniqueValues = keys.map((key) => {
    const values = options[key]?.values || [];
    return [
      ...new Set(
        values.map((value) => toLowercaseAndCapitalizeFirstChar(value))
      )
    ];
  });

  //Filter currentVariances
  const filteredVariances = currentVariances.filter(
    (variance) => variance.name.split(', ').length === keys.length
  );

  //get whole matrix count
  const totalVariancesCount = uniqueValues.reduce(
    (acc, arr) => acc * arr.length,
    1
  );

  if (totalVariancesCount === filteredVariances?.length)
    return currentVariances;
  else {
    const allVariancesOptions = getAllCombinations(uniqueValues);
    const existingVariancesOptions = currentVariances.map((item) =>
      toLowercaseAndCapitalizeFirstChar(item.name)
    );
    const remainingVariancesOptions = allVariancesOptions.filter(
      (item) =>
        !existingVariancesOptions.includes(
          toLowercaseAndCapitalizeFirstChar(item)
        )
    );

    const remainingVariances = remainingVariancesOptions.map((item) => {
      return {
        name: item,
        isActive: false,
        variantQuantity: 0,
        variantPrice: 0,
        options: item.split(', ').map((value) => {
          return findVarianceOptions(value, options);
        })
      };
    });
    return [...filteredVariances, ...remainingVariances];
  }
};

const checkIfVariant = (product) => {
  for (let key in product) {
    if (key.includes('option') && !!product[`${key}Value`]) {
      return !!product[`${key}Value`];
    }
  }
  return false; // No key contains "option"
};

const toLowercaseAndCapitalizeFirstChar = (str) => {
  if (str) {
    return (
      String(str).charAt(0).toUpperCase() + String(str).slice(1).toLowerCase()
    );
  }
};

const getVariantName = (currentProduct, variant) => {
  const options = [
    toLowercaseAndCapitalizeFirstChar(currentProduct.option1),
    toLowercaseAndCapitalizeFirstChar(currentProduct.option2),
    toLowercaseAndCapitalizeFirstChar(currentProduct.option3)
  ];

  const nameParts = [
    toLowercaseAndCapitalizeFirstChar(variant.option1Value),
    toLowercaseAndCapitalizeFirstChar(variant.option2Value),
    toLowercaseAndCapitalizeFirstChar(variant.option3Value)
  ];

  const filteredOptions = options.filter((option) => option !== undefined);
  const filteredNameParts = nameParts.filter((part) => part !== undefined);
  if (filteredOptions.length && filteredNameParts.length) {
    return filteredNameParts.join(', ');
  } else {
    return null;
  }
};

const groupProductWithVariances = (products) => {
  const groupedProducts = [];

  let currentProduct = null;
  for (const product of products) {
    if (product.name || product.nameAr || !checkIfVariant(product)) {
      if (currentProduct) {
        groupedProducts.push(currentProduct);
      }
      currentProduct = {
        ...product,
        name: product.name,
        description: product.description || '',
        defaultPrice: product.defaultPrice || 0,
        availableQuantity:
          product.availableQuantity >= 0 ? product.availableQuantity : 1,
        disabled: product.disabled === undefined ? true : !!product.disabled,
        originalPrice: product?.originalPrice || null
      };
    } else if (checkIfVariant(product) && currentProduct) {
      const variantName = getVariantName(currentProduct, product);
      if (!variantName) {
        continue;
      } else {
        const existingVariance =
          currentProduct?.productVariances &&
          currentProduct?.productVariances.find(
            (variance) =>
              variance?.name?.toLowerCase() === variantName.toLowerCase()
          );

        if (existingVariance) {
          continue;
        }

        const optionsOrder = getOptionsOrder(currentProduct, product);
        const option = {
          name: variantName,
          options: [
            !!product.option1Value && {
              optionName: toLowercaseAndCapitalizeFirstChar(
                currentProduct.option1
              ),
              optionValue: toLowercaseAndCapitalizeFirstChar(
                product.option1Value
              )
            },
            !!product.option2Value && {
              optionName: toLowercaseAndCapitalizeFirstChar(
                currentProduct.option2
              ),
              optionValue: toLowercaseAndCapitalizeFirstChar(
                product.option2Value
              )
            },
            !!product.option3Value && {
              optionName: toLowercaseAndCapitalizeFirstChar(
                currentProduct.option3
              ),
              optionValue: toLowercaseAndCapitalizeFirstChar(
                product.option3Value
              )
            }
          ].filter(Boolean),
          variantQuantity:
            product.availableQuantity >= 0 ? product.availableQuantity : 1,
          isActive: product.disabled === undefined ? true : !!product.disabled,
          variantDefaultImage: product.imageURL,
          variantOriginalPrice: product?.originalPrice || null,
          variantPrice: product.defaultPrice || 0,
          referenceId: product?.refId?.toString()
        };
        currentProduct.optionsOrder = {
          ...currentProduct.optionsOrder,
          ...optionsOrder
        };
        currentProduct.productVariances = currentProduct.productVariances
          ? [...currentProduct.productVariances, option]
          : [option];
      }
    }
  }

  currentProduct.productVariances = getAllVariances(
    currentProduct.optionsOrder,
    currentProduct.productVariances
  );

  if (currentProduct) {
    groupedProducts.push(currentProduct);
  }

  return groupedProducts;
};

export const convertXlsxToJSON = (file) => {
  return new Promise((resolve, reject) => {
    XlsxPopulate.fromDataAsync(file).then((workbook) => {
      const sheetRecords = workbook.sheet(0).usedRange().value();
      const rowsData = getXlsxRowsData(sheetRecords, PRODUCT_OBJ_MAPPING);
      const formattedData = groupProductWithVariances(rowsData);
      const header = sheetRecords[0].map((cell) =>
        cell instanceof XlsxPopulate.RichText
          ? cell.text().replace(/undefined/g, '')
          : cell
      );

      const error = getSheetFileError({
        header,
        rowsData: formattedData
      });

      if (error) {
        reject(error);
      }
      return resolve(formattedData);
    });
  });
};

export const sheetExceededMaxRows = ({ rows, maxRows }) => {
  return rows?.length > maxRows;
};

export const convertSheetToJSON = (file) => {
  switch (file?.type) {
    case XLSX:
      return convertXlsxToJSON(file);

    case CSV:
      return convertCSVToJSON(file);

    default:
      return;
  }
};

export const getCategoryName = (category) =>
  isDashboardArabic() && (category?.nameAr || category?.categoryNameAr)
    ? category?.nameAr || category?.categoryNameAr
    : category?.name || category?.categoryName;

export const getSubCategoryName = (subCategory) =>
  isDashboardArabic() && (subCategory?.subCategoryNameAr || subCategory?.nameAr)
    ? subCategory?.subCategoryNameAr || subCategory?.nameAr
    : subCategory?.subCategoryName || subCategory?.name;

export const getProductCategory = (category) => {
  if (!category) return {};
  const {
    name,
    nameAr,
    categoryName,
    categoryNameAr,
    subCategoryName,
    subCategoryNameAr,
    ...rest
  } = category;

  return {
    ...rest,
    categoryName: getCategoryName({
      name,
      nameAr,
      categoryName,
      categoryNameAr
    }),
    subCategoryName: getSubCategoryName({
      subCategoryName,
      subCategoryNameAr
    })
  };
};

export const getDeliveryFees = (businessInfo, previewLanguage) => {
  return [FREE_PLAN, CONDITIONAL_PLAN].includes(
    businessInfo?.storeInfo?.shippingConfig?.type
  )
    ? customizedFmt(previewLanguage)(
        {
          id: `products.sllr_store.setting_page.preview.${
            businessInfo?.storeInfo?.shippingConfig?.type === FREE_PLAN
              ? 'free_delivery_tag'
              : 'conditional_delivery_tag'
          }`
        },
        {
          value: customizedFmt(previewLanguage)({
            id: 'products.sllr_store.setting_page.preview.free_delivery_tag'
          }),
          amount: customizedFmt(previewLanguage)(
            {
              id: COUNTRY_CURRENCY
            },
            {
              cod: businessInfo?.storeInfo?.shippingConfig?.minimumCodLimit
            }
          )
        }
      )
    : businessInfo?.storeInfo?.shippingConfig?.type === FLAT_RATE_PLAN
    ? customizedFmt(previewLanguage)(
        {
          id: 'products.sllr_store.shipping_fees'
        },
        {
          value: customizedFmt(previewLanguage)(
            {
              id: COUNTRY_CURRENCY
            },
            {
              cod: businessInfo?.storeInfo?.shippingConfig?.addedCodAmount
            }
          )
        }
      )
    : businessInfo?.storeInfo?.shippingConfig?.type === CUSTOM_RATE_PLAN
    ? customizedFmt(previewLanguage)({
        id: 'products.sllr_store.at_checkout'
      })
    : '';
};

export const findLowerOriginalPriceIndices = (variants, rowIndex) => {
  return variants.reduce((indices, variant, index) => {
    if (
      variant?.variantOriginalPrice &&
      variant?.variantPrice &&
      variant.variantOriginalPrice < variant.variantPrice
    ) {
      indices.push(index + 1 + rowIndex);
    }
    return indices;
  }, []);
};
